import { useMutation, useQuery } from "@tanstack/react-query"
import { AxiosError } from "axios"

import { OrganizationsResponses } from "@tfg/types/endpoints/organizations"
import {
  SupplierGetQueryValidationType,
  supplierMonitoringFiltersValidationType,
  orderByValidationType,
} from "@tfg/types/schemas/organization/schemas"

import { httpClient } from "~/backend/axios.ts"

export const useDownloadSuppliersCSVMutation = ({
  searchSupplier,
  filters,
  orderBy,
}: Omit<SupplierGetQueryValidationType, "filters" | "orderBy"> & {
  filters?: Partial<supplierMonitoringFiltersValidationType>
  orderBy?: Partial<orderByValidationType>
}) => {
  return useMutation<void, AxiosError>({
    mutationKey: [
      "suppliers",
      "download-csv",
      searchSupplier,
      filters,
      orderBy,
    ],

    mutationFn: async () => {
      const response = await httpClient.get("suppliers/export-suppliers", {
        responseType: "blob",
        params: {
          searchSupplier,
          filters:
            filters && Object.keys(filters).length > 0
              ? JSON.stringify(filters)
              : undefined,
          orderBy:
            orderBy && Object.keys(orderBy).length > 0
              ? JSON.stringify(orderBy)
              : undefined,
        },
      })

      // anchor link
      const element = document.createElement("a")
      element.href = URL.createObjectURL(new Blob([response.data]))
      element.download = "suppliers.csv"

      // simulate link click
      document.body.appendChild(element) // Required for this to work in FireFox
      element.click()
    },
  })
}

export const useDownloadSuppliersProdSitesCSVMutation = ({
  searchProductionSite,
}: {
  searchProductionSite: string
}) => {
  return useMutation<void, AxiosError>({
    mutationKey: ["suppliers", "download-csv", searchProductionSite],

    mutationFn: async () => {
      const response = await httpClient.get("suppliers/export-prod-sites", {
        responseType: "blob",
        params: {
          searchProductionSite,
        },
      })

      // anchor link
      const element = document.createElement("a")
      element.href = URL.createObjectURL(new Blob([response.data]))
      element.download = "production-sites.csv"

      // simulate link click
      document.body.appendChild(element) // Required for this to work in FireFox
      element.click()
    },
  })
}

export const useListOrganizationContactsQuery = () => {
  return useQuery({
    queryKey: ["organizationId", "contacts"],

    queryFn: async () => {
      const response = await httpClient.get<OrganizationsResponses.Contact[]>(
        `organizations/contacts`
      )
      return response.data
    },
  })
}
